import { Tenant, SuperAdminFile } from "../../types/super-admin";
import query from "../../utils/query";
import { endpoints } from "./endpoints";
import { fetchApi, authHeaders, fileToBody } from "./http";

export const checkSuperAdmin = async (token: string): Promise<{ isSuperAdmin: boolean }> => {
    try {
        const result = await fetchApi<{ isSuperAdmin: boolean }>(endpoints.superAdminCheck, {
            method: "GET",
            headers: authHeaders(token, "application/json"),
        });
        if (!result.ok) {
            return { isSuperAdmin: false };
        }
        return result.data;
    } catch {
        return { isSuperAdmin: false };
    }
};

export const getTenants = async (token: string): Promise<Tenant[]> => {
    const result = await fetchApi<Tenant[]>(endpoints.superAdminTenants, {
        method: "GET",
        headers: authHeaders(token, "application/json"),
    });
    if (!result.ok) {
        throw new Error("Failed to fetch tenants");
    }
    return result.data;
};

export const getTenantExcludedFiles = async (token: string, tenantId: string): Promise<string[]> => {
    const result = await fetchApi<string[]>(`${endpoints.superAdminTenants}/${encodeURIComponent(tenantId)}/excluded-files`, {
        method: "GET",
        headers: authHeaders(token, "application/json"),
    });
    if (!result.ok) {
        throw new Error("Failed to fetch tenant excluded files");
    }
    return result.data;
};

export interface TenantPromptConfig {
    prompt: string;
    has_custom_prompt: boolean;
}

export const getTenantPrompt = async (token: string, tenantId: string): Promise<TenantPromptConfig> => {
    const result = await fetchApi<TenantPromptConfig>(`${endpoints.superAdminTenants}/${encodeURIComponent(tenantId)}/prompt`, {
        method: "GET",
        headers: authHeaders(token, "application/json"),
    });
    if (!result.ok) {
        throw new Error(result.error || "Failed to fetch tenant prompt");
    }
    return result.data;
};

export const updateTenantPrompt = async (
    token: string,
    tenantId: string,
    prompt: string,
): Promise<TenantPromptConfig> => {
    const result = await fetchApi<TenantPromptConfig>(`${endpoints.superAdminTenants}/${encodeURIComponent(tenantId)}/prompt`, {
        method: "PUT",
        headers: authHeaders(token, "application/json"),
        body: JSON.stringify({ prompt }),
    });
    if (!result.ok) {
        throw new Error(result.error || "Failed to update tenant prompt");
    }
    return result.data;
};

export const updateTenantExcludedFiles = async (
    token: string,
    tenantId: string,
    excludedFileIds: string[],
): Promise<string[]> => {
    const result = await fetchApi<string[]>(`${endpoints.superAdminTenants}/${encodeURIComponent(tenantId)}/excluded-files`, {
        method: "PUT",
        headers: authHeaders(token, "application/json"),
        body: JSON.stringify({ excluded_file_ids: excludedFileIds }),
    });
    if (!result.ok) {
        throw new Error(result.error || "Failed to update tenant excluded files");
    }
    return result.data;
};

export interface SuperAdminUser {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    tenant_id: string;
}

export const getAllUsers = async (token: string, tenantId?: string): Promise<SuperAdminUser[]> => {
    const url = tenantId
        ? query(endpoints.superAdminUsers, ["tenant_id", tenantId])
        : endpoints.superAdminUsers;
    const result = await fetchApi<SuperAdminUser[]>(url, {
        method: "GET",
        headers: authHeaders(token, "application/json"),
    });
    if (!result.ok) {
        throw new Error("Failed to fetch users");
    }
    return result.data;
};

export const uploadFile = async (
    token: string,
    file: File,
    tenantId: string,
    userId?: string,
): Promise<SuperAdminFile> => {
    const { body, contentType } = await fileToBody(file);

    const params = new URLSearchParams({
        filename: file.name,
        tenant_id: tenantId,
    });
    if (userId) {
        params.set("user_id", userId);
    }

    const result = await fetchApi<SuperAdminFile>(`${endpoints.superAdminFiles}?${params.toString()}`, {
        method: "POST",
        headers: authHeaders(token, contentType),
        body,
    });

    if (!result.ok) {
        throw new Error(result.error || "Failed to upload file");
    }

    return result.data;
};

export const getAllFiles = async (token: string, tenantId?: string): Promise<SuperAdminFile[]> => {
    const url = tenantId
        ? query(endpoints.superAdminFiles, ["tenant_id", tenantId])
        : endpoints.superAdminFiles;
    const result = await fetchApi<SuperAdminFile[]>(url, {
        method: "GET",
        headers: authHeaders(token, "application/json"),
    });
    if (!result.ok) {
        throw new Error("Failed to fetch files");
    }
    return result.data;
};

export const deleteFile = async (token: string, id: string): Promise<void> => {
    const result = await fetchApi<void>(`${endpoints.superAdminFiles}/${id}`, {
        method: "DELETE",
        headers: authHeaders(token),
    });
    if (!result.ok) {
        throw new Error(result.error || "Failed to delete file");
    }
};

export interface BulkDeleteResult {
    deleted: number;
    total: number;
    results: { id: string; success: boolean; error?: string }[];
}

export const bulkDeleteFiles = async (token: string, ids: string[]): Promise<BulkDeleteResult> => {
    const result = await fetchApi<BulkDeleteResult>(endpoints.superAdminFilesBulk, {
        method: "DELETE",
        headers: authHeaders(token, "application/json"),
        body: JSON.stringify({ ids }),
    });
    if (!result.ok) {
        throw new Error(result.error || "Failed to delete files");
    }
    return result.data;
};

export const getFileEventsUrl = (token: string): string => {
    return query(endpoints.superAdminFilesEvents, ["token", encodeURIComponent(token)]);
};

export const syncFiles = async (token: string): Promise<{ synced: number; failed: number }> => {
    const result = await fetchApi<{ synced: number; failed: number }>(endpoints.superAdminFilesSync, {
        method: "POST",
        headers: authHeaders(token, "application/json"),
    });
    if (!result.ok) {
        throw new Error("Failed to sync files");
    }
    return result.data;
};

export function getSuperAdminFileDownloadUrl(fileId: string, token: string): string {
    return query(`${endpoints.superAdminFiles}/${fileId}/download`, ["token", encodeURIComponent(token)]);
}
